import React from "react";

import { cn } from "@/lib/utils";

// shadcn
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";

// radix-ui
import { SelectProps } from "@radix-ui/react-select";

// services
import { currencyService, Currency } from '@/services/currencyService';

interface CurrencySelectProps extends Omit<SelectProps, "onValueChange"> {
  onValueChange?: (value: string) => void;
  onCurrencySelect?: (currency: Currency) => void;
  name: string;
  placeholder?: string;
  variant?: "default" | "small" | "icon-only";
  valid?: boolean;
  showFlag?: boolean;
  showSymbol?: boolean;
  className?: string;
}

const CurrencySelect = React.forwardRef<HTMLButtonElement, CurrencySelectProps>(
  (
    {
      value,
      onValueChange,
      onCurrencySelect,
      name,
      placeholder = "Select currency",
      variant = "default",
      valid = true,
      showFlag = true,
      showSymbol = true,
      className,
      ...props
    },
    ref
  ) => {
    const [currencies, setCurrencies] = React.useState<Currency[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [isOpen, setIsOpen] = React.useState(false);
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    // Fetch currencies from API
    React.useEffect(() => {
      const fetchCurrencies = async () => {
        try {
          setLoading(true);
          const currencyData = await currencyService.getAllCurrencies();
          setCurrencies(currencyData);
        } catch (error) {
          console.error('Error fetching currencies:', error);
          // Set empty array on error so component still renders
          setCurrencies([]);
        } finally {
          setLoading(false);
        }
      };

      fetchCurrencies();
    }, []);

    // Filter currencies based on search term
    const filteredCurrencies = React.useMemo(() => {
      if (!searchTerm) return currencies;
      return currencies.filter((currency: Currency) =>
        currency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        currency.symbol.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }, [currencies, searchTerm]);

    // Focus search input when dropdown opens
    React.useEffect(() => {
      if (isOpen && searchInputRef.current) {
        // Small delay to ensure the input is rendered
        setTimeout(() => {
          searchInputRef.current?.focus();
        }, 50);
      }
    }, [isOpen]);

    // Clear search when dropdown closes
    React.useEffect(() => {
      if (!isOpen) {
        setSearchTerm("");
      }
    }, [isOpen]);

    const handleValueChange = (newValue: string) => {
      const fullCurrencyData = currencies.find(
        (curr: Currency) => curr.code === newValue
      );
      if (fullCurrencyData) {
        if (onValueChange) {
          onValueChange(newValue);
        }
        if (onCurrencySelect) {
          onCurrencySelect(fullCurrencyData);
        }
      }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
      // Prevent the Select from closing when typing
      if (e.key !== "Escape" && e.key !== "Enter" && e.key !== "Tab") {
        e.stopPropagation();
      }
    };

    const handleSearchClick = (e: React.MouseEvent) => {
      // Prevent Select from handling clicks on the search input
      e.stopPropagation();
    };

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      // Ensure input stays focused after typing
      setTimeout(() => {
        if (searchInputRef.current) {
          searchInputRef.current.focus();
        }
      }, 0);
    };

    // Get the selected currency for display
    const currentCurrency = React.useMemo(() => {
      return currencies.find((curr: Currency) => curr.code === value);
    }, [value, currencies]);

    if (loading) {
      return (
        <Select disabled {...props} name={name}>
          <SelectTrigger
            className={cn(
              "w-full",
              variant === "small" && "w-fit gap-2",
              variant === "icon-only" && "w-10 justify-center",
              className
            )}
            ref={ref}
          >
            <SelectValue placeholder="Loading currencies..." />
          </SelectTrigger>
        </Select>
      );
    }

    return (
      <Select
        value={value}
        onValueChange={handleValueChange}
        onOpenChange={setIsOpen}
        open={isOpen}
        {...props}
        name={name}
        data-valid={valid}
      >
        <SelectTrigger
          className={cn(
            "w-full",
            variant === "small" && "w-fit gap-2",
            variant === "icon-only" && "w-10 justify-center",
            className
          )}
          data-valid={valid}
          ref={ref}
        >
          {currentCurrency ? (
            <SelectValue placeholder={placeholder}>
              <div className="flex items-center gap-2">
                <span className=" text-md">
                  {currentCurrency.name} ({currentCurrency.symbol})
                </span>
              </div>
            </SelectValue>
          ) : (
            <SelectValue placeholder={placeholder} />
          )}
        </SelectTrigger>
        <SelectContent>
          <div className="p-2" onClick={handleSearchClick}>
            <Input
              ref={searchInputRef}
              placeholder="Search currencies..."
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              onClick={handleSearchClick}
              onFocus={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
              className="mb-2"
              autoFocus
            />
          </div>
          <SelectGroup>
            {filteredCurrencies.length > 0 ? (
              filteredCurrencies.map((currency: Currency) => (
                <SelectItem key={currency.code} value={currency.code}>
                  <span className="text-md w-8 text-center">
                    {currency.name} ({currency.symbol})
                  </span>
                </SelectItem>
              ))
            ) : (
              <div className="px-2 py-1 text-sm text-muted-foreground">
                No currencies found
              </div>
            )}
          </SelectGroup>
        </SelectContent>
      </Select>
    );
  }
);

CurrencySelect.displayName = "CurrencySelect";

export { CurrencySelect };
export type { Currency };

// Re-export utility functions from the service
export { getCurrencySymbol, getCurrencyFlag, getCurrencyData, getCurrencySymbolAsync, getCurrencyFlagAsync, getCurrencyDataAsync } from '@/services/currencyService';

/**
 * Currency Select Component
 *
 * This component fetches currencies from the API and returns the currency CODE (e.g., "USD", "EUR") as the value,
 * while displaying the currency NAME and SYMBOL (e.g., "US Dollar ($)") in the UI.
 *
 * The component automatically fetches all active currencies from the `/api/v1/currencies` endpoint.
 *
 * Features:
 * - Search functionality with real-time filtering
 * - Seamless typing experience with auto-focus
 * - Searches by currency name, code, and symbol
 *
 * Usage examples:
 *
 * // Basic usage - shows name and symbol with API data
 * <CurrencySelect
 *   name="currency"
 *   value={currencyCode}
 *   onValueChange={setCurrencyCode}
 * />
 *
 * // Small variant - shows name and symbol in small format
 * <CurrencySelect
 *   name="currency"
 *   variant="small"
 *   value={currencyCode}
 *   onValueChange={setCurrencyCode}
 * />
 *
 * // Icon only - shows only the symbol
 * <CurrencySelect
 *   name="currency"
 *   variant="icon-only"
 *   value={currencyCode}
 *   onValueChange={setCurrencyCode}
 * />
 */